'use client';

import { c as _c } from "react/compiler-runtime";
import { useMarketOutcomeHues } from '@morfi/backend-sdk-v2/client';
import { isNoOutcomeTitle, isYesOutcomeTitle, type OutcomeHueName, type OutcomePaletteStep, type OutcomeRole, outcomeHueFromTitle, outcomeHuesForMarket, outcomeHueToStepVar, outcomeHueToVar } from '@/lib/chart/outcome-hue';

// Re-export for consumers (FQ-119: use this hook or `@/lib/chart/outcome-hue`).
export { outcomeHueToStepVar, outcomeHueToVar };
export type { OutcomeHueName, OutcomePaletteStep, OutcomeRole };
export type MarketOutcomeColorClasses = Omit<OutcomeColorClasses, 'hue'> & {
  readonly hue: OutcomeHueName | undefined;
};
const PENDING_COLORS: MarketOutcomeColorClasses = {
  outline: 'bg-background-tertiary',
  fill: 'bg-background-secondary',
  hoverFill: 'hover:bg-background-secondary',
  hoverOutline: 'hover:bg-background-tertiary',
  text: 'text-foreground-secondary',
  cta: 'bg-background-tertiary',
  ctaForeground: 'text-foreground-secondary',
  hue: undefined
};
export function useMarketOutcomeColor(marketPubkey, index) {
  const hue = useMarketOutcomeHues(marketPubkey)?.get(index);
  return hue === undefined ? PENDING_COLORS : classesForHue(hue);
}
export function useMarketOutcomeColors(marketPubkey, outcomes) {
  const $ = _c(5);
  const hues = useMarketOutcomeHues(marketPubkey);
  let t0;
  if ($[0] !== hues || $[1] !== outcomes) {
    let t1;
    if ($[3] !== hues) {
      t1 = outcome => {
        const hue = hues?.get(outcome.index);
        return hue === undefined ? PENDING_COLORS : classesForHue(hue);
      };
      $[3] = hues;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = outcomes.map(t1);
    $[0] = hues;
    $[1] = outcomes;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Tailwind class bundle returned for a single outcome.
 *
 * - `outline` / `fill` / `hoverFill` / `text` → bar + label pattern.
 * - `cta` / `ctaForeground` → solid CTA button pattern.
 * - `hue` → resolved `OutcomeHueName` (caller can compose other utilities,
 *   e.g. `border-outcome-${hue}-fill`).
 *
 * Class strings are theme-independent; the cascade resolves the actual color
 * via `:root` (light) and `.dark` (dark) blocks in `globals.css`.
 */
export interface OutcomeColorClasses {
  outline: string;
  fill: string;
  /** `hover:bg-outcome-{hue}-fill` — matches bar fill on button hover (safelisted for Tailwind v4 scan). */
  hoverFill: string;
  /** `hover:bg-outcome-{hue}-outline` — tinted hover for buttons that rest on `fill` (safelisted). */
  hoverOutline: string;
  text: string;
  cta: string;
  ctaForeground: string;
  hue: OutcomeHueName;
}
interface UseOutcomeColorOptions {
  /** Stable parent address; omit only for anonymous design previews. */
  marketPubkey?: string | undefined;
  /** Complete canonical set when rendering a single outcome or a subset. */
  marketOutcomes?: ReadonlyArray<OutcomeInput> | undefined;
  /**
   * Per-outcome hue override map keyed by `outcome.index`. When set, replaces
   * the palette slot before per-market collision-avoidance runs. Yes/No anchors
   * take precedence. Overrides participate in the strict-uniqueness check.
   */
  overrides?: Record<number, OutcomeHueName>;
}
interface OutcomeInput {
  title: string;
  index: number;
}

// ─── Referential-stability caches ───────────────────────────────────────────
// `useOutcomeColor` is a PURE function (no React hooks called) — its outputs
// depend only on the market address, titles, indices, and overrides. To let downstream
// memoization (the React Compiler in consumers, plus per-leaf field hooks)
// actually do its job, identical inputs must return the SAME refs. Without
// this, `CardBarGrid` (which reads `outcomes` — a fresh array of fresh
// objects on every conviction tick) was producing a fresh `c` prop for every
// bar, churning ~561 re-renders / 40 s of `MarketRowOutcomeLabelButton`.
//
// These are MODULE-LEVEL pure-function caches — NOT React hooks — so they
// are outside FQ-140's scope (the rule targets the React memoization hooks
// banned in `.claude/rules/frontend-conventions.md`). Bounded by the size of
// the palette + the count of distinct markets / outcome sets per session.
const hueClassesCache: Map<OutcomeHueName, OutcomeColorClasses> = new Map<OutcomeHueName, OutcomeColorClasses>();
function classesForHue(hue: OutcomeHueName): OutcomeColorClasses {
  const cached = hueClassesCache.get(hue);
  if (cached !== undefined) return cached;
  const result: OutcomeColorClasses = {
    outline: `bg-outcome-${hue}-outline`,
    fill: `bg-outcome-${hue}-fill`,
    hoverFill: `hover:bg-outcome-${hue}-fill`,
    hoverOutline: `hover:bg-outcome-${hue}-outline`,
    text: `text-outcome-${hue}-text`,
    cta: `bg-outcome-${hue}-cta`,
    ctaForeground: `text-outcome-${hue}-cta-foreground`,
    hue
  };
  hueClassesCache.set(hue, result);
  return result;
}

// Defensive soft cap — outcome sets per session are O(markets) so realistic
// usage stays well under this. Hitting it would indicate an unbounded source
// of new titles; a full clear is cheap and self-healing.
const ARRAY_RESULT_CACHE_MAX = 1024;
const arrayResultCache: Map<string, readonly OutcomeColorClasses[]> = new Map<string, readonly OutcomeColorClasses[]>();

/** Stable content-signature of the array-form inputs + overrides. */
function arrayResultCacheKey(outcomes: ReadonlyArray<OutcomeInput>, overrides: Record<number, OutcomeHueName> | undefined, marketPubkey: string | undefined): string {
  const itemsKey = outcomes.map(o => [o.title, o.index]);
  const overridesKey = overrides === undefined || Object.keys(overrides).length === 0 ? '' : JSON.stringify(overrides);
  return JSON.stringify([marketPubkey, itemsKey, overridesKey]);
}

/**
 * Resolve outcome color classes for one outcome OR an array of outcomes.
 *
 * **Yes/No reservation**: outcomes whose label is `"yes"` or a derivative
 * (`"Yes — ban them"`, `"Yes, definitely"`) always resolve to the
 * **outcome-teal** hue family on every surface; `"no"`-derivatives
 * (`"No — let people play"`) resolve to **outcome-red**. Other outcomes use
 * canonical-index slots in the market's seeded palette. The
 * reservation applies on every class field — `outline`, `fill`, `text`, `cta`,
 * and `ctaForeground` all root on the teal or red scale.
 *
 * **Single-outcome form** returns one `OutcomeColorClasses`. **Array form**
 * returns a parallel array with strict per-market hue uniqueness enforced
 * (no two outcomes in the same market share a hue; throws on > 10 outcomes).
 *
 * **Theme handling**: the hook is theme-independent — it returns Tailwind
 * class strings; the cascade resolves light/dark via the semantic-token block
 * in `globals.css`. There is no `useTheme()` call, no `dark:` prefix needed.
 *
 * @example Bar pattern (most common)
 * ```tsx
 * const { outline, fill, text } = useOutcomeColor({ title, index });
 * return (
 *   <div className={cn('rounded-md p-2', outline)}>
 *     <div className={cn('h-6 rounded-sm', fill)} style={{ width: `${share}%` }}>
 *       <span className={cn('px-2 text-caption font-medium', text)}>{title}</span>
 *     </div>
 *   </div>
 * );
 * ```
 *
 * @example CTA button (trade panel, opinion bet)
 * ```tsx
 * const { cta, ctaForeground } = useOutcomeColor({ title, index });
 * return <AnimatedButton className={cn(cta, ctaForeground)}>Buy {title}</AnimatedButton>;
 * ```
 *
 * @example Chart line stroke (recharts / lightweight-charts)
 * ```tsx
 * const { hue } = useOutcomeColor({ title, index });
 * return <Line stroke={outcomeHueToStepVar(hue, 400)} />;
 * ```
 *
 * @see Engineer onboarding: `frontend/specs/035-outcome-color-palette/supplementary/quickstart.md`
 * @see Palette generator: `frontend/scripts/generate-outcome-palette.py`
 * @see Spec FR-015 / FR-017 / FR-023a–d for the contract details.
 */
export function useOutcomeColor(outcome: OutcomeInput, options?: UseOutcomeColorOptions): OutcomeColorClasses;
export function useOutcomeColor(outcomes: ReadonlyArray<OutcomeInput>, options?: UseOutcomeColorOptions): readonly OutcomeColorClasses[];
export function useOutcomeColor(input: OutcomeInput | ReadonlyArray<OutcomeInput>, options?: UseOutcomeColorOptions): OutcomeColorClasses | readonly OutcomeColorClasses[] {
  if (options?.marketOutcomes !== undefined) {
    const canonical = options.marketOutcomes;
    const hues = outcomeHuesForMarket(canonical, 'precise-order', options.marketPubkey, options.overrides);
    const resolve = (outcome: OutcomeInput): OutcomeColorClasses => {
      const hue = hues[canonical.findIndex(candidate => candidate.index === outcome.index)];
      if (hue === undefined) throw new Error(`useOutcomeColor: missing market outcome ${String(outcome.index)}`);
      return classesForHue(hue);
    };
    return Array.isArray(input) ? input.map(resolve) : resolve(input as OutcomeInput);
  }
  if (Array.isArray(input)) {
    if (input.length === 0) return [];
    const overrides = options?.overrides;
    const cacheKey = arrayResultCacheKey(input, overrides, options?.marketPubkey);
    const cached = arrayResultCache.get(cacheKey);
    if (cached !== undefined) return cached;
    const hues = outcomeHuesForMarket(input, 'precise-order', options?.marketPubkey, overrides);
    const result: readonly OutcomeColorClasses[] = hues.map(classesForHue);
    if (arrayResultCache.size >= ARRAY_RESULT_CACHE_MAX) arrayResultCache.clear();
    arrayResultCache.set(cacheKey, result);
    return result;
  }
  const single = input as OutcomeInput;
  const overrides = options?.overrides;
  const overrideHue = overrides?.[single.index];
  if (overrideHue !== undefined && !isYesOutcomeTitle(single.title) && !isNoOutcomeTitle(single.title)) {
    return classesForHue(overrideHue);
  }
  // Precise-order default: the resolver reserves teal/red for Yes/No-derivative
  // labels and draws every other outcome from the non-reserved sequence by
  // position — so `title` AND `index` both matter.
  return classesForHue(outcomeHueFromTitle(single.title, single.index, 'precise-order', options?.marketPubkey));
}